import clsx from 'clsx';
import { FC } from 'react';
import Dropzone, { Accept, DropEvent, FileRejection } from 'react-dropzone';

import { convertToMimeTypes } from '~utils/convertToMimeTypes';
import uploadFilesStyles from './uploadFilesStyles.css';

export type UploadRejection = FileRejection;

interface IUploadInputProps {
  progress?: number;
  onUpload: (files: File[], fileRejections: UploadRejection[], event: DropEvent) => void;
  onUploadRejected?: (fileRejections: UploadRejection[], event: DropEvent) => void;
  theme?: 'dark' | 'light';
  options?: {
    mimeTypes?: string[][];
  };
  multiple: boolean;
  className?: string;
  overlayContentClassName?: string;
  containerClassName?: string;
  imageRatio?: boolean;
  allowedFormats?: string[];
}

export const UploadInput: FC<IUploadInputProps> = ({
  allowedFormats,
  onUpload,
  onUploadRejected,
  progress,
  theme,
  multiple,
  options,
  className,
  overlayContentClassName,
  containerClassName,
}: IUploadInputProps) => {
  const classes = clsx({
    [uploadFilesStyles.overlay]: true,
    [uploadFilesStyles.overlayWithProgress]: Boolean(progress),
    'u-absolute': true,
    'u-absolute--fill': true,
    [className]: className,
  });

  const outerClasses = clsx(
    {
      relative: true,
      'u-image-ratio--4-3': !containerClassName?.match(/u-image-ratio--/),
    },
    containerClassName,
  );

  const dropdownAreaClasses = clsx({
    'u-padding-vertical-lg': true,
    'u-padding-horizontal-md': true,
    'u-color-white': theme === 'dark',
  });

  let allowedMimeTypes: Accept = null;

  if (options && options.mimeTypes) {
    allowedMimeTypes = options.mimeTypes.reduce((acc, [mime, ext]) => {
      acc[mime] = ext ? ext.split('/') : [mime];
      return acc;
    }, {} as Accept);
  } else if (allowedFormats) {
    const mimeTypes = convertToMimeTypes(allowedFormats);
    allowedMimeTypes = mimeTypes.reduce((acc, [mime, ext]) => {
      acc[mime] = ext ? ext.split('/') : [mime];
      return acc;
    }, {} as Accept);
  }

  if (progress) {
    return (
      <div className={clsx(outerClasses, containerClassName)}>
        <div className={classes}>
          <div className={clsx(uploadFilesStyles.overlayContent, overlayContentClassName)}>
            <div className={clsx(uploadFilesStyles.overlayContentInner)}>
              <div>
                <strong>Uploading …</strong>
              </div>
              <div>{progress} %</div>
              <div className="c-progress c-progress--thin">
                <div className="c-progress__bar" style={{ width: `${progress}%` }} />
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={clsx(outerClasses, containerClassName)}>
      <Dropzone
        onDrop={onUpload}
        multiple={multiple}
        accept={allowedMimeTypes}
        onDropRejected={onUploadRejected}
        noDragEventsBubbling={true}
      >
        {({ getRootProps, getInputProps, isDragActive }) => {
          return (
            <div {...getRootProps()} className={clsx(classes, 'dropzone', { 'dropzone--isActive': isDragActive })}>
              <div className={uploadFilesStyles.overlayHeader} />
              <div className={clsx(uploadFilesStyles.overlayContent, overlayContentClassName)}>
                <div className={clsx(uploadFilesStyles.overlayContentInner)}>
                  <div className={dropdownAreaClasses}>
                    <input {...getInputProps()} />
                    <i className="c-icon-custom c-icon-custom--ic-upload u-size-lg u-block u-padding-bottom-xs" />
                    <div>
                      <strong>Drag & Drop</strong>
                    </div>
                    <div className="u-size-sm">a file to upload (or click to select)</div>
                  </div>
                </div>
              </div>
              <div className={uploadFilesStyles.overlayFooter}>
                <div className="o-text--center o-text--quiet u-size-sm u-margin-right-sm u-margin-bottom-xs">
                  {options
                    ? options.mimeTypes &&
                      options.mimeTypes
                        .map(mimeType => mimeType[1])
                        .filter(val => typeof val !== 'undefined')
                        .join(', ')
                    : allowedFormats
                      ? allowedFormats.join(', ')
                      : ''}
                </div>
              </div>
            </div>
          );
        }}
      </Dropzone>
    </div>
  );
};

export default UploadInput;
